var _excluded = ["input", "meta", "className", "enablePlaceholderOption", "options", "optionGroups", "placeholder"];
function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { blurDirty, fieldPropTypes, fieldOptionGroupsType, fieldOptionsType, hasInputError, omitLabelProps } from '../helpers';
import { LabeledField } from '../labels';
import { compose, filterInvalidDOMProps, generateInputErrorId, serializeOptions, serializeOptionGroups } from '../../utils';

/**
 *
 * A select input that can be used in a `redux-form`-controlled form.
 *
 * The value of each option is specified via the `options` or the `optionGroups` prop.
 * The `options` prop will be ignored if `optionGroups` is present.
 *
 * The `options` prop can either be:
 * - An array of strings
 * - An array of numbers
 * - An array of key-value pairs: `{ key, value }`
 *
 * The `optionGroups` props must be an array of objects with the following keys:
 * - `name`: The name of the option group
 * - `options`: As above, an array of strings or key-value pairs.
 *
 * The value of the `Select` component will be the same as the value of the selected option (converted to a string).
 *
 * @name Select
 * @type Function
 * @param {Object} input - A `redux-form` [input](http://redux-form.com/6.5.0/docs/api/Field.md/#input-props) object
 * @param {Object} meta - A `redux-form` [meta](http://redux-form.com/6.5.0/docs/api/Field.md/#meta-props) object
 * @param {Array} options - An array of option values (strings, numbers, or key-value pairs). This prop will be ignored if `optionGroups` is present.
 * @param {Array} optionGroups - An array of option group objects
 * @param {String} [placeholder='Select'] - A string to display as a placeholder option. Pass in `false` to hide the placeholder option.
 * @param {Boolean} [enablePlaceholderOption=false] - A flag indicating that the placeholder option should not be `disabled`
 * @example
 *
 * // With string options
 *
 * function PaintColorForm ({ handleSubmit, pristine, invalid, submitting }) {
 *   return (
 *     <form onSubmit={ handleSubmit }>
 *       <Field
 *          name="paintColor"
 *          component={ Select }
 *          options={[
 *            'Purple',
 *            'Green',
 *            'Magenta',
 *          ]}
 *       />
 *       <SubmitButton {...{ pristine, invalid, submitting }}>
 *         Submit
 *       </SubmitButton>
 *     </form>
 *   )
 * }
 *
 * // With object options
 *
 * function EmployeeForm ({ handleSubmit, pristine, invalid, submitting }) {
 *   return (
 *     <form onSubmit={ handleSubmit }>
 *       <Field
 *          name="employeeId"
 *          component={ Select }
 *          options={[
 *            { key: 'Janet', value: 100 },
 *            { key: 'Bob', value: 101 },
 *          ]}
 *       />
 *       <SubmitButton {...{ pristine, invalid, submitting }}>
 *         Submit
 *       </SubmitButton>
 *     </form>
 *   )
 * }
 */

var propTypes = _objectSpread(_objectSpread({}, fieldPropTypes), {}, {
  enablePlaceholderOption: PropTypes.bool,
  placeholder: PropTypes.string,
  options: fieldOptionsType,
  optionGroups: fieldOptionGroupsType
});
var defaultProps = {
  enablePlaceholderOption: false,
  options: [],
  optionGroups: [],
  placeholder: 'Select'
};
function Select(props) {
  var _omitLabelProps = omitLabelProps(props),
    _omitLabelProps$input = _omitLabelProps.input,
    name = _omitLabelProps$input.name,
    value = _omitLabelProps$input.value,
    onBlur = _omitLabelProps$input.onBlur,
    onChange = _omitLabelProps$input.onChange,
    meta = _omitLabelProps.meta,
    className = _omitLabelProps.className,
    enablePlaceholderOption = _omitLabelProps.enablePlaceholderOption,
    options = _omitLabelProps.options,
    optionGroups = _omitLabelProps.optionGroups,
    placeholder = _omitLabelProps.placeholder,
    rest = _objectWithoutProperties(_omitLabelProps, _excluded);
  var optionObjects = serializeOptions(options);
  var optionGroupObjects = serializeOptionGroups(optionGroups);
  return /*#__PURE__*/React.createElement(LabeledField, props, /*#__PURE__*/React.createElement("select", _objectSpread({
    id: name,
    className: classnames({
      unselected: value === ''
    }),
    name: name,
    value: value,
    onBlur: onBlur,
    onChange: onChange,
    'aria-describedby': hasInputError(meta) ? generateInputErrorId(name) : null
  }, filterInvalidDOMProps(rest)), placeholder && /*#__PURE__*/React.createElement("option", {
    value: "",
    disabled: !enablePlaceholderOption
  }, placeholder), optionGroupObjects.length ? optionGroupObjects.map(function (_ref, idx) {
    var name = _ref.name,
      options = _ref.options;
    return /*#__PURE__*/React.createElement("optgroup", {
      key: idx,
      label: name
    }, options.map(function (_ref2) {
      var key = _ref2.key,
        value = _ref2.value;
      return /*#__PURE__*/React.createElement("option", {
        key: value,
        value: value
      }, key);
    }));
  }) : optionObjects.map(function (_ref3) {
    var key = _ref3.key,
      value = _ref3.value;
    return /*#__PURE__*/React.createElement("option", {
      key: value,
      value: value
    }, key);
  })));
}
Select.propTypes = propTypes;
Select.defaultProps = defaultProps;
export default compose(blurDirty())(Select);